/**
 * =============================================================================
 * WlDistributionDonut
 *
 * Componente genérico de donut (pizza) com legenda lateral exibindo valor
 * formatado em R$ e percentual. Foi extraído de `WlRevenueByPlanPieChart`
 * (v1.1.0) para permitir reuso em outras distribuições financeiras do painel
 * WL (ex.: cobranças por meio de pagamento) sem duplicar componente de chart.
 *
 * Aceita uma lista genérica de itens `{ id, name, value }`. A ordenação vem
 * pronta do chamador; aqui filtramos apenas itens com `value > 0`.
 *
 * @version 1.0.0
 * RELEASE:
 *   2026-07-22 v1.0.0 - Versão inicial (extraída de WlRevenueByPlanPieChart).
 * =============================================================================
 */
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

// Paleta consistente com o restante dos gráficos WL Financeiro.
const COLORS = [
  'hsl(217 91% 60%)', // blue-500
  'hsl(142 71% 45%)', // green-500
  'hsl(38 92% 50%)',  // amber-500
  'hsl(280 65% 60%)', // purple-500
  'hsl(340 82% 52%)', // pink-500
  'hsl(190 90% 45%)', // cyan-500
  'hsl(0 84% 60%)',   // red-500
  'hsl(158 64% 40%)', // teal-600
];

/** Item genérico consumido pelo donut. */
export interface WlDistributionDonutItem {
  id: string;
  name: string;
  value: number;
}

interface Props {
  data: WlDistributionDonutItem[];
  /** Formata o valor (default: R$ pt-BR). */
  formatValue?: (v: number) => string;
  /** Altura total do container (default 260px). */
  height?: number;
}

const fmtBRLDefault = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    .format(Number(v || 0));

/** Donut + legenda customizada com valor absoluto e percentual. */
export function WlDistributionDonut({
  data,
  formatValue = fmtBRLDefault,
  height = 260,
}: Props) {
  // Descarta itens sem valor para não poluir a pizza com fatia zero.
  const rows = (data ?? []).filter((r) => Number(r.value) > 0);
  const total = rows.reduce((acc, r) => acc + Number(r.value || 0), 0);

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">Sem dados</p>;
  }

  return (
    <div className="flex flex-col w-full gap-3">
      {/* Donut no topo */}
      <div className="w-full" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={90}
              paddingAngle={2}
            >
              {rows.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number) => formatValue(Number(value))}
              contentStyle={{ fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legenda embaixo — grid responsivo, ocupa a largura toda. */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs w-full">
        {rows.map((r, i) => {
          const pct = total > 0 ? (Number(r.value) / total) * 100 : 0;
          return (
            <li key={r.id} className="flex items-center gap-2 min-w-0">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm shrink-0"
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className="truncate flex-1">{r.name}</span>
              <span className="font-medium tabular-nums">{formatValue(Number(r.value))}</span>
              <span className="text-muted-foreground tabular-nums w-12 text-right">
                ({pct.toFixed(1)}%)
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
